import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { AiChat } from "@/components/AiChat";
import { api } from "@/lib/api";
import { PHASE_ORDER } from "@/lib/phases";
import { useAuth } from "@/context/AuthContext";
import { DiagnoseBooks } from "@/components/DiagnoseBooks";
import { ConsentForms } from "@/components/consents/ConsentForms";
import { ActivityTab } from "@/components/ActivityTab";
import { ShareReport } from "@/components/ShareReport";
import { ClientReportShare } from "@/components/ClientReportShare";
import { ReceiptScanner } from "@/components/ReceiptScanner";
import { ReceiptEmailInbox } from "@/components/ReceiptEmailInbox";
import { Books } from "@/components/Books";
import { Accounting } from "@/components/Accounting";
import { Receivables } from "@/components/Receivables";
import { Payables } from "@/components/Payables";
import { CashFlowForecast } from "@/components/CashFlowForecast";
import { Taxes } from "@/components/Taxes";
import { VaultSection } from "@/components/VaultSection";
import { DocumentIntake } from "@/components/DocumentIntake";
import { BookingHistory } from "@/components/BookingHistory";
import { WaitlistPanel } from "@/components/WaitlistPanel";
import { ReviewQueue } from "@/components/ReviewQueue";
import { DemoResetButton } from "@/components/DemoResetButton";
import { Invoices } from "@/components/Invoices";
import { Jobs } from "@/components/Jobs";
import { EquipmentScheduler } from "@/components/EquipmentScheduler";
import { Supplies } from "@/components/Supplies";
import { Memberships } from "@/components/Memberships";
import { RecurringInvoices } from "@/components/RecurringInvoices";
import { ContractorTools } from "@/components/ContractorTools";
import { Mileage } from "@/components/Mileage";
import { Estimates } from "@/components/Estimates";
import { ClientPortalManager } from "@/components/ClientPortalManager";
import { AgricultureProduction } from "@/components/AgricultureProduction";
import { AgricultureSquareSync } from "@/components/AgricultureSquareSync";
import { BusinessSettingsPanel } from "@/components/BusinessSettingsPanel";
import { VendorInsightsPanel } from "@/components/VendorInsightsPanel";
import { PeriodClose } from "@/components/PeriodClose";
import { ServiceBuilder } from "@/components/ServiceBuilder";
import { MembershipTracker } from "@/components/MembershipTracker";
import { BookingPageManager } from "@/components/BookingPageManager";
import { RevenueInsights } from "@/components/RevenueInsights";
import { SchedulerImport } from "@/components/SchedulerImport";
import { CustomersDirectory } from "@/components/CustomersDirectory";
import { SectionNav } from "@/components/workspace/SectionNav";
import { SectionHeader } from "@/components/workspace/SectionHeader";
import { HomeOverview } from "@/components/workspace/HomeOverview";
import { SetupGuides } from "@/components/workspace/SetupGuides";
import { toolsFor, isAgricultureBusiness, isWellnessBusiness } from "@/lib/industries";
import { defaultBusinessTab, filterBusinessTools, isFullBusinessRole } from "@/lib/teamAccess";
import { SECTIONS, SETUP_PHASES, MONEY_ACTIONS, resolveView, viewsFor, sectionOf } from "@/lib/workspaceNav";
import { CaretLeft } from "@phosphor-icons/react";

const STICKY_OFFSET = 84;
let settleCancel = null;
const cancelSettle = () => { if (settleCancel) settleCancel(); };

const clickWhenReady = (testid, attempts = 8) => {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (el) {
    cancelSettle();
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.click();
  } else if (attempts > 0) {
    setTimeout(() => clickWhenReady(testid, attempts - 1), 150);
  }
};

const scrollToTestId = (testid, attempts = 8) => {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (el) { cancelSettle(); window.scrollTo({ top: Math.max(0, window.scrollY + el.getBoundingClientRect().top - STICKY_OFFSET), behavior: "smooth" }); }
  else if (attempts > 0) setTimeout(() => scrollToTestId(testid, attempts - 1), 150);
};

// After a section/sub-tab change, make sure the destination screen is actually on screen (under the sticky header).
// `force` always snaps the anchor under the sticky bar (instant) so a screen never inherits an old scroll position.
// Anchor: "section-nav" for main-section changes and first load (section pills + header visible), "business-tabs" for sub-tabs.
const revealWorkspace = (attempts = 6, force = false, anchor = "business-tabs") => {
  const el = document.querySelector(`[data-testid="${anchor}"]`) || document.querySelector('[data-testid="business-tabs"]');
  if (!el) { if (attempts > 0) setTimeout(() => revealWorkspace(attempts - 1, force, anchor), 120); return; }
  const top = el.getBoundingClientRect().top;
  if (force || top < STICKY_OFFSET - 24 || top > window.innerHeight * 0.4) {
    window.scrollTo({ top: Math.max(0, window.scrollY + top - STICKY_OFFSET), behavior: force ? "auto" : "smooth" });
  }
};

// Keep the anchor pinned under the sticky bar while the new screen loads (in-flight wheel/trackpad animations and
// late-arriving content both push it off). Stops early once the user scrolls on purpose.
const SETTLE_MS = 1800;
const SETTLE_GRACE_MS = 400;
const settleWorkspace = (anchor = "business-tabs") => {
  cancelSettle();
  const started = performance.now();
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    settleCancel = null;
    ["wheel", "touchstart", "keydown", "pointerdown"].forEach((evt) => window.removeEventListener(evt, onIntent));
  };
  const onIntent = () => { if (performance.now() - started > SETTLE_GRACE_MS) stop(); };
  ["wheel", "touchstart", "keydown", "pointerdown"].forEach((evt) => window.addEventListener(evt, onIntent, { passive: true }));
  settleCancel = stop;
  const tick = () => {
    if (stopped) return;
    if (performance.now() - started > SETTLE_MS) return stop();
    const el = document.querySelector(`[data-testid="${anchor}"]`) || document.querySelector('[data-testid="business-tabs"]');
    if (el) {
      const drift = el.getBoundingClientRect().top - STICKY_OFFSET;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const target = Math.min(maxScroll, Math.max(0, window.scrollY + drift));
      if (Math.abs(target - window.scrollY) > 2) window.scrollTo({ top: target, behavior: "auto" });
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
};

export default function BusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwnerMode = (user?.mode || "bookkeeper") === "owner";
  const [business, setBusiness] = useState(null);
  const [guides, setGuides] = useState({});
  const [view, setView] = useState("home");
  const [setupPhase, setSetupPhase] = useState("fundamentals");
  const [invRefresh, setInvRefresh] = useState(0);
  const [trackerRefresh, setTrackerRefresh] = useState(0);
  const [scanNonce, setScanNonce] = useState(0);
  const [addCustomerNonce, setAddCustomerNonce] = useState(0);
  const [uploadNonce, setUploadNonce] = useState(0);
  const [resetNonce, setResetNonce] = useState(0);
  const [attention, setAttention] = useState(0);
  const lastViewBySection = useRef({});
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilter = searchParams.get("category") || "";
  const focusTransactionId = searchParams.get("txn") || "";
  const focusReceiptId = searchParams.get("receipt") || "";
  const receiptFocus = searchParams.get("receipt_gap") || "";

  const goToView = useCallback((key, anchor = "business-tabs") => {
    if (!key) return;
    if (SETUP_PHASES.has(key)) setSetupPhase(key);
    const resolved = resolveView(key);
    setView(resolved);
    lastViewBySection.current[sectionOf(resolved)] = resolved;
    revealWorkspace(15, true, anchor);
    // Deep links (?txn= / ?receipt= / ?receipt_gap=) scroll to their own target — don't pin the anchor over them.
    const params = new URLSearchParams(window.location.search);
    if (!["txn", "receipt", "receipt_gap"].some((k) => params.get(k))) settleWorkspace(anchor);
  }, []);

  const openReceipts = useCallback((autoScan = false, receiptId = "") => {
    if (autoScan) { goToView("scan"); setScanNonce((n) => n + 1); return; }
    if (receiptId) { setSearchParams({ tab: "receipts", receipt: receiptId }); return; }
    goToView("receipts");
  }, [goToView, setSearchParams]);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t === "receipts" && searchParams.get("receipt")) goToView("receipts");
    else if (t === "receipts") openReceipts();
    else if (t) goToView(t, "section-nav");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const h = () => openReceipts(true);
    window.addEventListener("bk-open-scan", h);
    return () => window.removeEventListener("bk-open-scan", h);
  }, [openReceipts]);

  useEffect(() => {
    api.get(`/businesses/${id}`).then((r) => setBusiness(r.data)).catch(() => {
      toast.error("Business not found");
      navigate("/dashboard");
    });
    Promise.all(PHASE_ORDER.map((p) => api.get(`/guides/${p}`).then((r) => [p, r.data]))).then((entries) =>
      setGuides(Object.fromEntries(entries))
    );
    api.get(`/businesses/${id}/alerts`).then((r) => setAttention((r.data || []).length)).catch(() => {});
  }, [id, navigate]);

  const toggle = async (p, stepId, done) => {
    setBusiness((b) => {
      const cs = { ...(b.completed_steps || {}) };
      const list = new Set(cs[p] || []);
      done ? list.add(stepId) : list.delete(stepId);
      cs[p] = [...list];
      const progress = { ...b.progress, [p]: { done: cs[p].length, total: b.progress[p].total } };
      return { ...b, completed_steps: cs, progress };
    });
    try {
      const { data } = await api.post(`/businesses/${id}/steps`, { phase: p, step_id: stepId, done });
      setBusiness(data);
      if (done) toast.success("Step completed");
    } catch {
      toast.error("Failed to save");
    }
  };

  const saveStepData = async (stepId, payload) => {
    const { data } = await api.put(`/businesses/${id}/steps/${stepId}/data`, payload);
    setBusiness(data);
  };

  const businessType = business?.business_type || "";
  const isAgriculture = isAgricultureBusiness(business || {});
  const isWellness = isWellnessBusiness(business || {});
  const allToolKeys = useMemo(() => {
    const base = [...toolsFor(businessType), ...(isAgriculture ? ["production", "square_sync"] : [])];
    if (isWellness) base.push("wellness_ops");
    return [...new Set(base)];
  }, [businessType, isAgriculture, isWellness]);
  const toolKeys = useMemo(() => {
    const filtered = filterBusinessTools(allToolKeys, business || {});
    if (business && isFullBusinessRole(business) && !filtered.includes("business_settings")) filtered.push("business_settings");
    if (business && !filtered.includes("activity")) filtered.push("activity");
    return filtered;
  }, [allToolKeys, business]);
  const visiblePhases = useMemo(() => (business?.permissions?.can_view_setup === false ? [] : PHASE_ORDER), [business?.permissions?.can_view_setup]);
  const views = useMemo(() => (business ? viewsFor(business, toolKeys, { hasPhases: visiblePhases.length > 0 }) : []), [business, toolKeys, visiblePhases]);
  const availableSections = useMemo(() => SECTIONS.map((s) => s.key).filter((k) => views.some((v) => v.section === k)), [views]);

  useEffect(() => {
    if (!business || !views.length) return;
    if (!views.some((v) => v.key === view)) {
      const fallback = defaultBusinessTab(business, toolKeys);
      const resolved = resolveView(fallback);
      goToView(views.some((v) => v.key === resolved) && !SETUP_PHASES.has(fallback) ? resolved : "home", "section-nav");
    }
  }, [business, view, views, toolKeys, goToView]);

  const section = sectionOf(view);
  const sectionViews = views.filter((v) => v.section === section);

  const changeSection = (key) => {
    const remembered = lastViewBySection.current[key];
    const first = views.find((v) => v.section === key)?.key;
    goToView(remembered && views.some((v) => v.key === remembered) ? remembered : first, "section-nav");
  };

  const runAction = (sec, item) => {
    if (sec === "home") return openReceipts(true);
    if (sec === "money") {
      const ctx = MONEY_ACTIONS[view];
      if (ctx && !ctx.click) return openReceipts(true);
      if (ctx?.click) return setTimeout(() => clickWhenReady(ctx.click), 60);
      goToView("books"); return setTimeout(() => clickWhenReady("toggle-add-txn"), 120);
    }
    if (sec === "customers") { goToView("customers"); return setAddCustomerNonce((n) => n + 1); }
    if (sec === "business") {
      if (item === "service") { goToView("services"); return setTimeout(() => clickWhenReady("new-service-btn"), 120); }
      if (item === "supply") { goToView("supplies"); return setTimeout(() => clickWhenReady("new-supply-btn"), 120); }
      if (item === "vendor") { goToView("ap"); toast("Vendors are added the moment you record their first bill.", { description: "Rowan will remember them for next time." }); return setTimeout(() => clickWhenReady("ap-new-bill-button"), 120); }
      if (item === "staff") { goToView("business_settings"); return setTimeout(() => scrollToTestId("business-settings-team-card"), 160); }
    }
    if (sec === "reports" && item) return goToView(item);
    if (sec === "vault") { goToView("vault"); return setUploadNonce((n) => n + 1); }
  };

  const businessMenu = useMemo(() => [
    ...(views.some((v) => v.key === "services") ? [{ key: "service", label: "Service or package" }] : []),
    ...(views.some((v) => v.key === "supplies") ? [{ key: "supply", label: "Supply item" }] : []),
    ...(views.some((v) => v.key === "ap") ? [{ key: "vendor", label: "Vendor (via first bill)" }] : []),
    ...(views.some((v) => v.key === "business_settings") ? [{ key: "staff", label: "Staff member" }] : []),
  ], [views]);
  const reportsMenu = useMemo(() => [
    ...(views.some((v) => v.key === "reports") ? [{ key: "reports", label: "Profit & loss / balance sheet" }] : []),
    ...(views.some((v) => v.key === "forecast") ? [{ key: "forecast", label: "Cash flow forecast" }] : []),
    ...(views.some((v) => v.key === "taxes") ? [{ key: "taxes", label: "Tax estimate & year-end pack" }] : []),
    ...(views.some((v) => v.key === "insights") ? [{ key: "insights", label: "Sales insights" }] : []),
    ...(views.some((v) => v.key === "diagnose") ? [{ key: "diagnose", label: "Diagnose my books" }] : []),
  ], [views]);

  if (!business) {
    return (
      <div className="min-h-screen">
        <Header />
        <p className="max-w-7xl mx-auto px-6 py-12" style={{ color: "var(--bk-text-2)" }}>Loading…</p>
      </div>
    );
  }

  const isContractor = business.business_type === "Contractor";
  const full = isFullBusinessRole(business);

  const renderView = () => {
    switch (view) {
      case "home": return <HomeOverview business={business} phases={visiblePhases} onGoToView={goToView} onScan={() => openReceipts(true)} onAction={runAction} />;
      case "scan": return <DocumentIntake businessId={business.id} autoOpen={scanNonce} onFiled={(r) => { if (!r?.open) return; if (r.kind === "receipt") openReceipts(false, r.data?.id); else goToView("vault"); }} />;
      case "review": return <ReviewQueue businessId={business.id} />;
      case "bookings": return <BookingHistory businessId={business.id} onOpenScheduler={() => goToView("scheduler")} />;
      case "noshows": return <BookingHistory key="missed" businessId={business.id} mode="missed" onOpenScheduler={() => goToView("scheduler")} />;
      case "waitlist": return <WaitlistPanel businessId={business.id} />;
      case "setup_guides": return <SetupGuides business={business} guides={guides} phases={visiblePhases} showRoadmap={toolKeys.includes("onboarding")} initialPhase={setupPhase} isOwnerMode={isOwnerMode} onToggle={toggle} onSaveStepData={saveStepData} />;
      case "activity": return <ActivityTab businessId={business.id} canViewAll={full} />;
      case "books": return <Books businessId={business.id} business={business} isAgriculture={isAgriculture} categoryFilter={categoryFilter} focusTransactionId={focusTransactionId} permissions={business.permissions} />;
      case "receipts": return (
        <div className="space-y-6">
          <ReceiptEmailInbox businessId={business.id} />
          <ReceiptScanner businessId={business.id} business={business} isAgriculture={isAgriculture} autoScan={0} focusMode={receiptFocus} focusReceiptId={focusReceiptId} />
        </div>
      );
      case "invoices": return (
        <div className="space-y-6">
          <RecurringInvoices businessId={business.id} onGenerated={() => setInvRefresh((n) => n + 1)} />
          <Invoices key={invRefresh} businessId={business.id} />
        </div>
      );
      case "ar": return <Receivables businessId={business.id} />;
      case "ap": return <Payables businessId={business.id} />;
      case "accounting": return <Accounting businessId={business.id} businessName={business.name} permissions={business.permissions} />;
      case "close": return <PeriodClose businessId={business.id} />;
      case "customers": return <CustomersDirectory businessId={business.id} openAddNonce={addCustomerNonce} onGoToView={goToView} />;
      case "consents": return <ConsentForms businessId={business.id} onGoToView={goToView} />;
      case "scheduler": return <EquipmentScheduler businessId={business.id} wellness={isWellness} />;
      case "booking": return <BookingPageManager businessId={business.id} />;
      case "import": return <SchedulerImport businessId={business.id} onDone={() => setTrackerRefresh((n) => n + 1)} />;
      case "memberships": return <Memberships businessId={business.id} />;
      case "members": return <MembershipTracker businessId={business.id} refreshKey={trackerRefresh} />;
      case "estimates": return <Estimates businessId={business.id} />;
      case "jobs": return <Jobs businessId={business.id} />;
      case "portal": return <ClientPortalManager businessId={business.id} />;
      case "services": return <ServiceBuilder businessId={business.id} onChanged={() => setTrackerRefresh((n) => n + 1)} />;
      case "supplies": return <Supplies businessId={business.id} />;
      case "vendors": return <VendorInsightsPanel businessId={business.id} title="Vendors" subtitle="Who you buy from most, and where spend is drifting up." testIdPrefix="vendors" />;
      case "business_settings": return full ? <BusinessSettingsPanel business={business} onBusinessChange={setBusiness} /> : null;
      case "vault": return <VaultSection businessId={business.id} uploadNonce={uploadNonce} onOpenReceipts={(receiptId) => openReceipts(false, receiptId)} />;
      case "mileage": return <Mileage businessId={business.id} business={business} />;
      case "contractor": return isContractor ? <ContractorTools businessId={business.id} /> : null;
      case "production": return isAgriculture ? <AgricultureProduction businessId={business.id} business={business} onBusinessChange={setBusiness} /> : null;
      case "square_sync": return isAgriculture ? <AgricultureSquareSync businessId={business.id} /> : null;
      case "reports": return <Accounting key="reports" businessId={business.id} businessName={business.name} permissions={business.permissions} initialView="reports" initialReport="profit_and_loss" />;
      case "forecast": return <CashFlowForecast businessId={business.id} businessName={business.name} />;
      case "taxes": return <Taxes businessId={business.id} entityState={business.state} business={business} />;
      case "insights": return <RevenueInsights businessId={business.id} />;
      case "diagnose": return <DiagnoseBooks businessId={business.id} initial={business.diagnosis} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="bk-page-shell py-10 pb-32">
        <Link to="/dashboard" data-testid="back-to-dashboard" className="inline-flex items-center gap-1.5 text-sm font-medium mb-6 hover:underline" style={{ color: "var(--bk-text-2)" }}>
          <CaretLeft size={16} weight="bold" /> Back to dashboard
        </Link>

        <div className="mb-8 flex flex-wrap items-start justify-between gap-4 rounded-[28px] p-6 sm:p-8 relative overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.96), rgba(255,249,240,0.95))", border: "1px solid rgba(229,231,235,0.85)", boxShadow: "0 12px 30px rgba(26,39,68,0.06)" }}>
          <div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold px-3 py-1.5 rounded-full mb-4" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }}>Your business workspace</span>
            {business.is_demo && <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-semibold px-3 py-1.5 rounded-full mb-4 ml-2" style={{ background: "#FFF3DC", color: "#B08840" }} data-testid="demo-badge">Demo data</span>}
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "var(--bk-text)" }} data-testid="business-title">
              {business.name}
            </h1>
            <p className="text-base mt-1" style={{ color: "var(--bk-text-2)" }}>
              {[business.entity_type, business.industry].filter(Boolean).join(" · ") || "Add details anytime"}
            </p>
          </div>
          {full && (
            <div className="flex flex-wrap gap-3">
              {business.is_demo && user?.role === "admin" && (
                <DemoResetButton business={business} onReset={() => { api.get(`/businesses/${id}`).then((r) => setBusiness(r.data)); api.get(`/businesses/${id}/alerts`).then((r) => setAttention((r.data || []).length)).catch(() => {}); goToView("home"); setResetNonce((n) => n + 1); }} />
              )}
              <ClientReportShare businessId={business.id} shareId={business.client_report_share_id} onChange={(sid) => setBusiness((b) => ({ ...b, client_report_share_id: sid }))} />
              <ShareReport businessId={business.id} shareId={business.share_id} onChange={(sid) => setBusiness((b) => ({ ...b, share_id: sid }))} />
            </div>
          )}
        </div>

        <SectionNav active={section} available={availableSections} onChange={changeSection} onScan={() => openReceipts(true)} attention={attention} />

        {searchParams.get("qb_offer") === "1" && (
          <div className="rounded-2xl border px-5 py-4 flex flex-wrap items-center justify-between gap-3 mb-6" style={{ borderColor: "rgba(232,168,56,0.4)", background: "var(--bk-rowan-bg)" }} data-testid="qb-offer-card">
            <div>
              <p className="font-semibold" style={{ color: "var(--bk-text)" }}>Switching from QuickBooks?</p>
              <p className="text-sm mt-0.5" style={{ color: "var(--bk-text-2)" }}>Rowan can import your transactions, chart of accounts, and categories from QuickBooks exports in minutes.</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => navigate(`/business/${id}?tab=books&qb=open`)} className="bk-btn-primary px-4 py-2 text-sm font-semibold" data-testid="qb-offer-import-btn">Import from QuickBooks</button>
              <button onClick={() => navigate(`/business/${id}`, { replace: true })} className="px-4 py-2 text-sm font-semibold border rounded-full" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} data-testid="qb-offer-dismiss-btn">Maybe later</button>
            </div>
          </div>
        )}

        <div data-testid="business-tabs" style={{ minHeight: `calc(100vh - ${STICKY_OFFSET}px)` }}>
          <SectionHeader section={section} views={sectionViews} active={view} onChange={goToView} onAction={runAction}
            menu={section === "business" ? businessMenu : section === "reports" ? reportsMenu : null}
            secondary={section === "home" && views.some((v) => v.key === "diagnose") ? { label: "Diagnose my books", testid: "main-action-diagnose", onClick: () => goToView("diagnose") } : null} />
          <div data-testid={`view-${view}`} key={`${view}-${resetNonce}`}>{renderView()}</div>
        </div>
      </main>
      <AiChat businessId={business.id} businessName={business.name} />
    </div>
  );
}
